import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { getRolesWithModulesAction, deleteRoleAction } from "../../../store/actions/RoleAction";
import Loader from "../Common/Loader";

const Role = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.role.roles);
  
  const [searchQueries, setSearchQueries] = useState({
    role_id: "",
    role_name: "",
    role_status: "",
  });
  
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState("role_id");
  const [sortOrder, setSortOrder] = useState("asc");
  const sort = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [test, setTest] = useState(0);
  
  // Add debounce timer ref
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      setError("");
      try {
        setLoading(true);
        await dispatch(getRolesWithModulesAction());
      } catch (err) {
        console.error("Failed to fetch roles:", err);
        setError("Failed to load Roles. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [dispatch]);

  const fetchRolesFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = sortField,
    sort_order = sortOrder,
    pagination = true,
    pageid = 0
  ) => {
    try {
      await dispatch(getRolesWithModulesAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order));
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setError("Failed to load Roles. Please try again.");
    }
  };

  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    
    // Clear all search queries first, then set only the current field
    const clearedQueries = {
      role_id: "",
      role_name: "",
      role_status: "",
    };
    
    // Update search queries - clear all others and set only the current field
    const updatedQueries = { ...clearedQueries, [field]: keyword };
    setSearchQueries(updatedQueries);
    setCurrentPage(0);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce the server call to avoid too many requests
    searchTimeoutRef.current = setTimeout(() => {
      // Only search if there's a keyword, otherwise fetch all data
      if (keyword.trim()) {
        fetchRolesFromServer(field, keyword, sortField, sortOrder, true, 0);
      } else {
        // If search is cleared, fetch all data without search parameters
        fetchRolesFromServer("", "", sortField, sortOrder, true, 0);
      }
    }, 300); // 300ms delay
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    fetchRolesFromServer("", "", field, order, true, currentPage);
  };

  const filteredData = roles
    .filter((role) =>
      Object.keys(searchQueries).every((key) =>
        role[key]?.toString().toLowerCase().includes(searchQueries[key].toLowerCase())
      )
    )
    .sort((a, b) => {
      const modifier = sortOrder === "asc" ? 1 : -1;
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortField === "role_id") {
        return (Number(aValue) - Number(bValue)) * modifier;
      }
      
      return aValue.toString().localeCompare(bValue.toString(), undefined, { sensitivity: 'base' }) * modifier;
    });

  const totalPages = Math.ceil(filteredData.length / sort);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchQueries, sortField, sortOrder]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const pagination = Array(Math.ceil(filteredData.length / sort))
    .fill()
    .map((_, i) => i + 1);

  const onClick = (i) => {
    setCurrentPage(i);
  };

  const handleEdit = (role) => {
    navigate(`/edit-role/${role.role_id}`, {
      state: { role },
    });
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this role!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          await dispatch(deleteRoleAction(id));
          await dispatch(getRolesWithModulesAction());
        } catch (error) {
          setError("Failed to delete role");
          console.error("Failed to delete role:", error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link to="/add-role" className="btn btn-primary btn-rounded add-role">
            + Add Role
          </Link>
        </div>
      </div>
      
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("role_id")} style={{ cursor: "pointer" }}>
                            Role ID{" "}
                            {sortField === "role_id" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.role_id}
                              onChange={(e) => handleSearch(e, "role_id")}
                              placeholder="Search ID"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("role_name")} style={{ cursor: "pointer" }}>
                            Role Name{" "}
                            {sortField === "role_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.role_name}
                              onChange={(e) => handleSearch(e, "role_name")}
                              placeholder="Search Role"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("role_status")} style={{ cursor: "pointer" }}>
                            Status{" "}
                            {sortField === "role_status" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.role_status}
                              onChange={(e) => handleSearch(e, "role_status")}
                              placeholder="Search Status"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length > 0 ? (
                          filteredData
                            .slice(currentPage * sort, (currentPage + 1) * sort)
                            .map((role) => (
                              <tr key={role.role_id}>
                                <td>{role.role_id}</td>
                                <td>{role.role_name}</td>
                                <td>
                                  <span
                                    className={`badge px-3 py-1 rounded-pill text-white ${
                                      role.role_status.toLowerCase() === "active"
                                        ? "bg-success"
                                        : "bg-danger"
                                    }`}
                                  >
                                    {role.role_status}
                                  </span>
                                </td>
                                <td className="text-center align-middle">
                                  <div className="d-inline-flex gap-3">
                                    <button
                                      className="btn btn-link p-0"
                                      onClick={() => handleEdit(role)}
                                      aria-label="Edit"
                                      title="Edit Role"
                                    >
                                      <i className="fa fa-pencil-alt fs-5 text-success"></i>
                                    </button>
                                    <button
                                      className="btn btn-link p-0"
                                      onClick={() => handleDelete(role.role_id)}
                                      aria-label="Delete"
                                      title="Delete Role"
                                    >
                                      <i className="fa fa-trash fs-5 text-danger"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-4">
                              No roles found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      Showing {filteredData.length === 0 ? 0 : currentPage * sort + 1} to{" "}
                      {Math.min((currentPage + 1) * sort, filteredData.length)} of {filteredData.length} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages);
                        
                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => setCurrentPage(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>
                          );
                          if (startPage > 1) {
                            pages.push(
                              <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            );
                          }
                        }
                        
                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i;
                          pages.push(
                            <button
                              key={i}
                              onClick={() => setCurrentPage(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>
                          );
                        }
                        
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>
                            );
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => setCurrentPage(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>
                          );
                        }
                        return pages;
                      })()}
                      
                      <button
                        // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Role;
